import { Button, MessageBar, MessageBarBody } from '@fluentui/react-components';
import { useTranslation } from '../../i18n';
import type { DrillState } from './productivityDrill';

/**
 * What a drill-through target says about itself (spec §5). The count it names is the server's list
 * of rows behind the segment, and when fewer are shown it says so and why, rather than letting a
 * smaller table pass for the segment.
 *
 * `noun` picks whole SENTENCES, not a word to splice in (Spanish UI): "Las 2 revisiones…" and
 * "Los 2 clientes…" differ in article and agreement, so a noun dropped into one English frame cannot
 * be right in both languages.
 */
export function DrillBanner({ state, shown, noun, onClear }: {
  state: DrillState;
  /** Rows actually on screen after every other filter. */
  shown: number;
  /** What the rows are: reviews (the Reviews grid) or clients (the Clients grid). */
  noun: 'review' | 'client';
  onClear: () => void;
}) {
  const { t } = useTranslation();
  if (state.kind === 'none') return null;
  const clear = (
    <Button size="small" appearance="transparent" onClick={onClear}>
      {t('drill.showAll')}
    </Button>
  );

  if (state.kind === 'invalid') {
    return (
      <MessageBar intent="warning" style={{ marginBottom: '12px' }}>
        <MessageBarBody>{t('drill.invalid')} {clear}</MessageBarBody>
      </MessageBar>
    );
  }
  if (state.kind === 'loading') {
    return (
      <MessageBar intent="info" style={{ marginBottom: '12px' }}>
        <MessageBarBody>{t(`drill.loading.${noun}`, { trimester: state.key.trimester, year: state.key.year })}</MessageBarBody>
      </MessageBar>
    );
  }
  if (state.kind === 'unavailable') {
    return (
      <MessageBar intent="warning" style={{ marginBottom: '12px' }}>
        <MessageBarBody>
          {t(`drill.unavailable.${noun}`, { trimester: state.key.trimester, year: state.key.year })} {clear}
        </MessageBarBody>
      </MessageBar>
    );
  }
  const total = state.drill.ids.size;
  return (
    <MessageBar intent="info" style={{ marginBottom: '12px' }}>
      <MessageBarBody>
        {t(`drill.behind.${noun}`, {
          count: total,
          label: state.drill.label,
          months: t('drill.months', { count: state.drill.months }),
        })}
        {shown < total ? ` ${t('drill.shown', { count: shown })}` : ''}{' '}
        {clear}
      </MessageBarBody>
    </MessageBar>
  );
}
